/**
 * Achar e REESCREVER os conectores da etapa 3 onde eles de fato moram.
 *
 * ★ **O cartão desenha `ui.props.conectores`, e esse valor é um RETRATO do
 * turno**: o servidor o apurou uma vez (`core/onboarding/conectores.py`) e o
 * gravou em `_mensagens.tool_calls` (JSONB). Nada relê. Quem conecta e volta à
 * tela vê o retrato antigo — é o buraco que estas funções fecham.
 *
 * ⚠️ **São DOIS lugares, e patchar um só não repinta a tela.**
 * `renderiza-partes.js::parteDaChamada` consulta a parte VIVA do slice
 * (`toolPartPorId`) ANTES da cópia persistida, e o slice só é esvaziado no
 * INÍCIO do turno seguinte. Logo: depois do OAuth (recarga de página) vale a
 * mensagem; depois do modal do WhatsApp (mesma página) vale a parte viva.
 *
 * ★ O discriminante da etapa 3 é `props.conectores` ser array — nenhuma outra
 * etapa o carrega. Casar pelo id da etapa seria uma terceira grafia de
 * `'conectar_ferramentas'`, livre para divergir.
 *
 * ⚠️ Reescrita IMUTÁVEL e MÍNIMA: só o caminho que mudou ganha objeto novo, e
 * as chaves do conector são preservadas — `ITEM_CONECTOR`
 * (`ui-registry/plataforma.js`) é `additionalProperties: false`, e um campo a
 * mais reprovaria o cartão inteiro.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { ehCartaoDeOnboarding } from '@modules/aawz-intelligence-v2/ui-registry/chave-do-onboarding'
import { ESTADO_CONECTADO } from '@modules/aawz-intelligence-v2/constants/Conectores'

/** Os conectores desta chamada/parte, ou `[]` quando não é a etapa 3. */
const conectoresDaParte = (parte) => {
  if (!ehCartaoDeOnboarding(parte)) return []
  const props = (parte.ui && parte.ui.props) || {}
  return Array.isArray(props.conectores) ? props.conectores : []
}

const chamadasDaMensagem = (mensagem) =>
  (mensagem && Array.isArray(mensagem.tool_calls) ? mensagem.tool_calls : [])

/**
 * Os ids dos conectores que AINDA não estão conectados, em todo o thread.
 *
 * ⚠️ `desconhecido` entra: leitura que falhou é justamente o que vale a pena
 * tentar de novo. Vazio significa "não há o que reconferir".
 *
 * @param {Array} mensagens `messages` do store.
 * @returns {Array<String>}
 */
export function conectoresPendentes(mensagens) {
  const lista = Array.isArray(mensagens) ? mensagens : []
  const ids = {}
  lista.forEach((mensagem) => {
    chamadasDaMensagem(mensagem).forEach((chamada) => {
      conectoresDaParte(chamada).forEach((conector) => {
        if (!conector || !conector.id) return
        if (conector.estado === ESTADO_CONECTADO) return
        ids[conector.id] = true
      })
    })
  })
  return Object.keys(ids)
}

/**
 * O id da mensagem que carrega a etapa 3 — a chave do `watch` que dispara a
 * releitura.
 *
 * ★ É o ID, e não os estados: ele não muda quando a releitura patcha o thread,
 * então o watcher dispara UMA vez por conversa aberta. Observar os estados
 * faria a própria correção pedir outra leitura.
 *
 * @param {Array} mensagens
 * @returns {?String}
 */
export function idDaMensagemComConectores(mensagens) {
  const lista = Array.isArray(mensagens) ? mensagens : []
  for (let i = 0; i < lista.length; i += 1) {
    const mensagem = lista[i]
    const temEtapa = chamadasDaMensagem(mensagem).some(
      (chamada) => conectoresDaParte(chamada).length > 0
    )
    if (temEtapa) return (mensagem && mensagem.id) || null
  }
  return null
}

/** A lista com os estados apurados aplicados — a MESMA referência se nada mudou. */
export function conectoresAtualizados(conectores, estados) {
  const mapa = estados || {}
  let mudou = false
  const nova = conectores.map((conector) => {
    if (!conector || !conector.id) return conector
    const estado = mapa[conector.id]
    if (!estado || estado === conector.estado) return conector
    mudou = true
    return { ...conector, estado: estado }
  })
  return mudou ? nova : conectores
}

/** O `ui` com os conectores atualizados — a MESMA referência se nada mudou. */
const uiAtualizado = (parte, estados) => {
  const conectores = conectoresDaParte(parte)
  if (!conectores.length) return parte.ui
  const novos = conectoresAtualizados(conectores, estados)
  if (novos === conectores) return parte.ui
  const props = { ...parte.ui.props, conectores: novos }
  return { ...parte.ui, props: props }
}

const chamadaAtualizada = (chamada, estados) => {
  const ui = uiAtualizado(chamada || {}, estados)
  if (!ui || ui === (chamada && chamada.ui)) return chamada
  return { ...chamada, ui: ui }
}

/**
 * As mensagens com os estados aplicados — a MESMA referência se nada mudou.
 *
 * @param {Array} mensagens
 * @param {Object} estados `{ [id do conector]: estado }`, só os apurados.
 * @returns {Array}
 */
export function mensagensComConectores(mensagens, estados) {
  const lista = Array.isArray(mensagens) ? mensagens : []
  let mudou = false
  const nova = lista.map((mensagem) => {
    const chamadas = chamadasDaMensagem(mensagem)
    if (!chamadas.length) return mensagem
    let mudouAqui = false
    const novas = chamadas.map((chamada) => {
      const atualizada = chamadaAtualizada(chamada, estados)
      if (atualizada !== chamada) mudouAqui = true
      return atualizada
    })
    if (!mudouAqui) return mensagem
    mudou = true
    return { ...mensagem, tool_calls: novas }
  })
  return mudou ? nova : lista
}

/**
 * O mapa de partes VIVAS com os estados aplicados — a MESMA referência se nada
 * mudou. É o slice `toolParts`, chaveado por `toolCallId`.
 *
 * @param {Object} partes `state.toolParts`.
 * @param {Object} estados
 * @returns {Object}
 */
export function partesComConectores(partes, estados) {
  const mapa = partes || {}
  let mudou = false
  const novo = {}
  Object.keys(mapa).forEach((id) => {
    const parte = mapa[id]
    const ui = uiAtualizado(parte || {}, estados)
    if (!ui || ui === (parte && parte.ui)) {
      novo[id] = parte
      return
    }
    mudou = true
    novo[id] = { ...parte, ui: ui }
  })
  return mudou ? novo : mapa
}
